import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, KeyRound } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) { setError("Lien invalide : le jeton de réinitialisation est manquant. Redemandez un lien depuis la page de connexion."); return; }
    if (password.length < 8) { setError("Le mot de passe doit contenir au moins 8 caractères."); return; }
    if (password !== confirm) { setError("Les deux mots de passe ne correspondent pas."); return; }
    setSaving(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setSuccess(true);
      setTimeout(() => navigate("/connexion"), 3000);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Ce lien est invalide, expiré ou déjà utilisé. Redemandez un lien depuis la page de connexion.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-ae flex min-h-[60vh] items-center justify-center py-16" data-testid="reset-password-page">
      <div className="card-ae w-full max-w-md p-8">
        {success ? (
          <div className="text-center" data-testid="reset-success">
            <CheckCircle2 className="mx-auto h-12 w-12 text-gold" aria-hidden />
            <h1 className="mt-4 font-heading text-2xl text-deepgreen">Mot de passe défini</h1>
            <p className="mt-3 text-sm text-muted-foreground">
              Votre nouveau mot de passe est enregistré. Vous allez être redirigé vers la page de connexion…
            </p>
            <Link to="/connexion" className="btn-primary-ae mt-6 inline-flex w-full justify-center" data-testid="reset-goto-login">
              Aller à la connexion
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-2xl text-deepgreen" data-testid="reset-title">Définir mon mot de passe</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Choisissez votre nouveau mot de passe (8 caractères minimum). Ce lien est à usage unique et expire après 1 heure.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="reset-form">
              <div>
                <label className="label-ae" htmlFor="reset-password">Nouveau mot de passe</label>
                <input
                  id="reset-password" type="password" className="input-ae" autoComplete="new-password"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  data-testid="reset-password-input"
                />
              </div>
              <div>
                <label className="label-ae" htmlFor="reset-password-confirm">Confirmer le mot de passe</label>
                <input
                  id="reset-password-confirm" type="password" className="input-ae" autoComplete="new-password"
                  value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  data-testid="reset-password-confirm-input"
                />
              </div>
              {error && <p className="rounded-lg bg-red-50 px-4 py-2.5 text-sm font-semibold text-red-800" data-testid="reset-error">{error}</p>}
              <button type="submit" disabled={saving} className="btn-primary-ae w-full disabled:opacity-50" data-testid="reset-submit-button">
                <KeyRound className="h-4 w-4" /> {saving ? "Enregistrement…" : "Enregistrer mon mot de passe"}
              </button>
            </form>
            <p className="mt-4 text-center text-xs text-muted-foreground">
              Lien expiré ? Retournez sur la <Link to="/connexion" className="font-semibold text-deepgreen underline underline-offset-2 hover:text-gold" data-testid="reset-back-login">page de connexion</Link> pour en redemander un.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
